/**
 * @fileoverview Local-first system-web service for department code-set vendor configuration.
 * @module system-web/code-set-vendor-service
 *
 * Responsibilities:
 * - Read and update tenant regulatory framework data through system Dexie.
 * - Enqueue inherently-online vendor validation, catalog, hydrate, and rehydrate operations.
 *
 * Dependencies:
 * - `@firesquirrel/local-db` for system Dexie access and sync wakeups.
 * - `apiClient` for online-only enqueue/catalog/credential operations.
 *
 * Architecture:
 * - Dexie remains the source of truth for UI state.
 * - API calls are restricted to background job enqueue and vendor operations that cannot be local-only.
 *
 * Notes:
 * - Do not add mock fallbacks or localStorage-backed services here.
 */
import { getSystemDb, wakeLocalWrite } from '@firesquirrel/local-db';
import { liveQuery, type Observable } from 'dexie';
import { api } from '../apiClient';
import { writeAuditLog } from './systemAuditLogService';

/**
 * @contract VendorBook
 *
 * Purpose:
 * - Represents a vendor book assigned to or available for a department code-set hydration flow.
 *
 * Fields:
 * - bookId: Vendor book identifier.
 * - hydrationOperation: Last tenant operation queued or completed for this book.
 *
 * Rules:
 * - `rehydrate` still uses the same cache validation/download path as initial hydrate.
 *
 * Sync:
 * - Stored in system Dexie through the tenant regulatory framework projection.
 */
export interface VendorBook {
  bookId: string;
  title: string;
  edition: string;
  chapterCount?: number;
  downloadStatus?: 'pending' | 'downloading' | 'completed' | 'failed';
  downloadedAt?: string;
  violationCount?: number;
  downloadJobId?: string;
  downloadPhase?: string;
  progressPercent?: number;
  processedRecords?: number;
  rawArtifactId?: string;
  codeSetId?: string;
  cacheHit?: boolean;
  errorMessage?: string;
  hydrationOperation?: 'hydrate' | 'rehydrate';
}

export interface VendorEntry {
  vendorId: 'icc' | 'nfpa' | 'custom';
  vendorName: string;
  isEnabled: boolean;
  credentials: {
    clientId?: string;
    clientSecret?: string;
    apiKey?: string;
    subscriptionId?: string;
  };
  selectedBooks?: VendorBook[];
  lastValidatedAt?: string;
  lastValidationStatus?: 'valid' | 'invalid' | 'untested';
  addedAt?: string;
  addedBy?: string;
}

export interface RegulatoryFramework {
  primaryFireCode?: 'IFC' | 'NFPA' | 'state_specific' | 'custom';
  codeEdition?: string;
  jurisdictionVariant?: string;
  codeSetVendors?: VendorEntry[];
}

export interface DownloadJob {
  jobId: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  vendorId: string;
  bookId: string;
  title: string;
  edition: string;
  processedRecords: number;
  totalRecords?: number;
  chapterCount?: number;
  violationCount: number;
  phase?: string;
  progressPercent?: number;
  message?: string;
  rawArtifactId?: string;
  codeSetId?: string;
  cacheHit?: boolean;
  error?: string;
  completedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CatalogBook {
  bookId: string;
  title: string;
  edition: string;
  chapterCount?: number;
}

async function getTenantFromDexie(tenantId: string): Promise<any | null> {
  try {
    const db = await getSystemDb();
    const all = await db.tenants.toArray() as any[];
    const found = all.find((t: any) => t._id === tenantId || t.id === tenantId);
    return found ?? null;
  } catch {
    return null;
  }
}

async function saveRegulatoryFrameworkLocal(
  tenantId: string,
  updater: (current: RegulatoryFramework, tenant: any) => RegulatoryFramework,
): Promise<RegulatoryFramework> {
  const db = await getSystemDb();
  const tenant = await getTenantFromDexie(tenantId);
  if (!tenant) throw new Error('Tenant not found in local cache');
  const next = updater((tenant.regulatoryFramework ?? {}) as RegulatoryFramework, tenant);
  await db.tenants.put({
    ...tenant,
    regulatoryFramework: next,
    updatedAt: new Date().toISOString(),
  });
  wakeLocalWrite('tenants');
  return next;
}

function mergeVendorEntry(
  existing: VendorEntry | undefined,
  incoming: Partial<VendorEntry> & { vendorId: string },
): VendorEntry {
  const now = new Date().toISOString();
  return {
    vendorId: incoming.vendorId as VendorEntry['vendorId'],
    vendorName: incoming.vendorName ?? existing?.vendorName ?? incoming.vendorId,
    isEnabled: incoming.isEnabled ?? existing?.isEnabled ?? true,
    credentials: {
      clientId: incoming.credentials?.clientId ?? existing?.credentials?.clientId,
      subscriptionId: incoming.credentials?.subscriptionId ?? existing?.credentials?.subscriptionId,
      clientSecret: incoming.credentials?.clientSecret ?? existing?.credentials?.clientSecret,
      apiKey: incoming.credentials?.apiKey ?? existing?.credentials?.apiKey,
    },
    selectedBooks: existing?.selectedBooks ?? incoming.selectedBooks ?? [],
    lastValidatedAt: existing?.lastValidatedAt ?? incoming.lastValidatedAt,
    lastValidationStatus: existing?.lastValidationStatus ?? incoming.lastValidationStatus ?? 'untested',
    addedAt: existing?.addedAt ?? incoming.addedAt ?? now,
    addedBy: existing?.addedBy ?? incoming.addedBy ?? 'system-admin',
  };
}

function isSameVendorBook(left: Pick<VendorBook, 'bookId' | 'edition'>, right: Pick<VendorBook, 'bookId' | 'edition'>): boolean {
  return left.bookId === right.bookId && String(left.edition ?? '') === String(right.edition ?? '');
}

export async function getRegulatoryFramework(tenantId: string): Promise<RegulatoryFramework> {
  const cached = await getTenantFromDexie(tenantId);
  return (cached?.regulatoryFramework ?? {}) as RegulatoryFramework;
}

/**
 * @process WatchRegulatoryFramework
 *
 * Steps:
 * 1. Open the system Dexie database.
 * 2. Read the requested tenant row from the local `tenants` table.
 * 3. Emit the current regulatory framework whenever Dexie changes.
 *
 * Inputs:
 * - tenantId: System tenant id.
 *
 * Outputs:
 * - Observable regulatory framework snapshot.
 *
 * Failure Handling:
 * - Emits an empty framework when the tenant is not present in local cache.
 *
 * Idempotency:
 * - REQUIRED
 */
export function watchRegulatoryFramework(tenantId: string): Observable<RegulatoryFramework> {
  return liveQuery(async () => {
    const cached = await getTenantFromDexie(tenantId);
    return (cached?.regulatoryFramework ?? {}) as RegulatoryFramework;
  });
}

export async function upsertVendor(tenantId: string, vendor: Partial<VendorEntry> & { vendorId: string }): Promise<VendorEntry> {
  let savedVendor: VendorEntry | undefined;
  let isNew = false;
  await saveRegulatoryFrameworkLocal(tenantId, (current) => {
    const vendors = [...(current.codeSetVendors ?? [])];
    const index = vendors.findIndex((entry) => entry.vendorId === vendor.vendorId);
    isNew = index < 0;
    const merged = mergeVendorEntry(index >= 0 ? vendors[index] : undefined, vendor);
    if (index >= 0) vendors[index] = merged;
    else vendors.push(merged);
    savedVendor = merged;
    return { ...current, codeSetVendors: vendors };
  });

  try {
    await writeAuditLog({
      collection: 'tenants',
      documentId: tenantId,
      action: isNew ? 'create' : 'update',
      summary: `${isNew ? 'Added' : 'Updated'} code-set vendor: ${vendor.vendorId}`,
      changes: [{ field: `regulatoryFramework.codeSetVendors[${vendor.vendorId}]`, oldValue: isNew ? null : '(previous)', newValue: vendor.vendorId, fieldType: 'string' }],
    });
  } catch { /* audit failure must not block mutation */ }

  return savedVendor!;
}

/**
 * @process RemoveTenantCodeSetBookAssignment
 *
 * Steps:
 * 1. Load the tenant regulatory framework from system Dexie.
 * 2. Remove the matching selected book from the requested vendor entry.
 * 3. Persist the updated regulatory framework locally.
 * 4. Wake background sync so MongoDB receives the metadata cleanup.
 *
 * Inputs:
 * - tenantId, vendorId, bookId, and edition for the selected book row.
 *
 * Outputs:
 * - Updated regulatory framework after local persistence.
 *
 * Failure Handling:
 * - Throws when the tenant is missing from local cache.
 *
 * Idempotency:
 * - REQUIRED
 */
export async function removeTenantBook(
  tenantId: string,
  data: { vendorId: string; bookId: string; edition: string },
): Promise<RegulatoryFramework> {
  const result = await saveRegulatoryFrameworkLocal(tenantId, (current) => ({
    ...current,
    codeSetVendors: (current.codeSetVendors ?? []).map((vendor) => (
      vendor.vendorId !== data.vendorId
        ? vendor
        : {
          ...vendor,
          selectedBooks: (vendor.selectedBooks ?? []).filter((book) => !isSameVendorBook(book, data)),
        }
    )),
  }));

  try {
    await writeAuditLog({
      collection: 'tenants',
      documentId: tenantId,
      action: 'update',
      summary: `Removed code-set book: ${data.bookId} (${data.edition}) from vendor ${data.vendorId}`,
      changes: [{ field: `regulatoryFramework.codeSetVendors[${data.vendorId}].selectedBooks`, oldValue: data.bookId, newValue: null, fieldType: 'string' }],
    });
  } catch { /* audit failure must not block mutation */ }

  return result;
}

export async function validateVendor(
  tenantId: string,
  vendorId: string,
  data?: Partial<VendorEntry> & { vendorId?: string },
): Promise<{ valid: boolean; status: string; message?: string; validatedAt: string }> {
  const result = await api.codeSetVendors.validateVendor(tenantId, vendorId, data as Record<string, unknown> | undefined);
  await saveRegulatoryFrameworkLocal(tenantId, (current) => ({
    ...current,
    codeSetVendors: (current.codeSetVendors ?? []).map((vendor) => (
      vendor.vendorId !== vendorId
        ? vendor
        : {
          ...vendor,
          lastValidationStatus: result.status as VendorEntry['lastValidationStatus'],
          lastValidatedAt: result.validatedAt,
        }
    )),
  }));

  try {
    await writeAuditLog({
      collection: 'tenants',
      documentId: tenantId,
      action: 'update',
      summary: `Validated vendor credentials: ${vendorId} — ${result.status}`,
      changes: [{ field: `regulatoryFramework.codeSetVendors[${vendorId}].lastValidationStatus`, oldValue: null, newValue: result.status, fieldType: 'string' }],
    });
  } catch { /* audit failure must not block mutation */ }

  return result as { valid: boolean; status: string; message?: string; validatedAt: string };
}

export async function getCatalog(tenantId: string, vendorId: string): Promise<{ books: CatalogBook[]; notice?: string }> {
  return api.codeSetVendors.getCatalog(tenantId, vendorId) as Promise<{ books: CatalogBook[]; notice?: string }>;
}

export async function triggerDownload(tenantId: string, data: { vendorId: string; bookId: string; edition: string; title: string; operation?: 'download' | 'rehydrate' }): Promise<{ jobId: string; status: string }> {
  return api.codeSetVendors.triggerDownload(tenantId, data) as Promise<{ jobId: string; status: string }>;
}

export async function rehydrateTenantBook(tenantId: string, data: { vendorId: string; bookId: string; edition: string; title: string }): Promise<{ jobId: string; status: string }> {
  return api.codeSetVendors.triggerDownload(tenantId, { ...data, operation: 'rehydrate' }) as Promise<{ jobId: string; status: string }>;
}

export async function getDownloadStatus(tenantId: string, jobId: string): Promise<DownloadJob> {
  return api.codeSetVendors.downloadStatus(tenantId, jobId) as Promise<DownloadJob>;
}
